import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  name: string;
  plan: 'free' | 'starter' | 'pro';
  whatsappNumber?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserProfile = async (userId: string, authUser?: SupabaseUser | null) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (profile) {
        setUser({
          id: profile.user_id,
          email: profile.email || '',
          name: profile.display_name || 'User',
          plan: (profile.plan as 'free' | 'starter' | 'pro') || 'free',
          whatsappNumber: profile.whatsapp_number || ''
        });
      } else if (authUser) {
        // Use provided auth user data if no profile exists
        setUser({
          id: authUser.id,
          email: authUser.email || '',
          name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.user_metadata?.display_name || 'User',
          plan: 'free',
          whatsappNumber: ''
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Fallback to basic session user data if available
      if (authUser) {
        setUser({
          id: authUser.id,
          email: authUser.email || '',
          name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.user_metadata?.display_name || 'User',
          plan: 'free',
          whatsappNumber: ''
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let profileFetchedForSession = false;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        
        if (session?.user) {
          if (!profileFetchedForSession || event === 'SIGNED_IN') {
             profileFetchedForSession = true;
             fetchUserProfile(session.user.id, session.user);
          }
        } else {
          profileFetchedForSession = false; // Reset on logout
          setUser(null);
          setIsLoading(false);
        }
      }
    );

    // Initial check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user && !profileFetchedForSession) {
        profileFetchedForSession = true;
        fetchUserProfile(session.user.id, session.user);
      } else if (!session) {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // Don't set loading false here - let onAuthStateChange handle it
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  const signup = async (name: string, email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const redirectUrl = `${window.location.origin}/confirm-signup`;
      
      // Use standard Supabase signup with email confirmation
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: name,
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // Welcome email is now sent automatically via database trigger
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const logout = async (): Promise<void> => {
    await supabase.auth.signOut();
  };

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const redirectUrl = `${window.location.origin}/dashboard`;
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const redirectUrl = `${window.location.origin}/reset-password-confirm`;
      
      // Use custom edge function for better email templates
      const { error } = await supabase.functions.invoke('send-reset-password-email', {
        body: {
          email,
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  // DEV ONLY: set VITE_BYPASS_AUTH=true in .env.local to preview protected pages without logging in.
  // import.meta.env.DEV is false in production builds, so this can never activate there.
  if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
    const devUser: User = { id: 'dev-preview-user', email: 'dev@localhost', name: 'Dev Preview', plan: 'pro' };
    return (
      <AuthContext.Provider value={{ user: user ?? devUser, login, signup, signInWithGoogle, logout, resetPassword, isLoading: false }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, signup, signInWithGoogle, logout, resetPassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};